import { useState } from 'react';
import { ActionIcon, Button, Group, Modal, Stack, TextInput } from '@mantine/core';
import { IconPencil } from '@tabler/icons-react';
import { useGraphService } from '@repo/services';
import type { DriveItem } from './driveUtils';
import classes from './RenameDriveFileButton.module.css';

interface RenameDriveFileButtonProps {
  driveId: string;
  item: DriveItem;
  onRenamed: (id: string, name: string) => void;
}

/** Renames a drive file while preserving its extension and reporting Graph errors inline. */
export function RenameDriveFileButton({ driveId, item, onRenamed }: RenameDriveFileButtonProps) {
  const { graphClient } = useGraphService();
  const [opened, setOpened] = useState(false);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const extension = item.name.match(/\.[^.]+$/)?.[0] ?? '';
  const originalBase = extension ? item.name.slice(0, -extension.length) : item.name;

  const save = async () => {
    const base = name.trim();
    if (!graphClient || saving || !base) return;
    if (/["*:<>?\/\\|]/.test(base) || base.endsWith('.') || `${base}${extension}`.length > 255) {
      setError('Use a name of at most 255 characters without " * : < > ? / \\ | or a trailing period.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const renamed = await graphClient.api(`/drives/${driveId}/items/${item.id}`).patch({
        name: `${base}${extension}`,
        '@microsoft.graph.conflictBehavior': 'fail',
      });
      onRenamed(item.id, renamed.name);
      setOpened(false);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not rename this file. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <ActionIcon
        className={classes.rename}
        variant='subtle'
        size='sm'
        aria-label={`Rename ${item.name}`}
        title='Rename file'
        disabled={!graphClient}
        onClick={(event) => {
          event.stopPropagation();
          setName(originalBase);
          setError(null);
          setOpened(true);
        }}
      >
        <IconPencil size={16} />
      </ActionIcon>
      <Modal opened={opened} onClose={() => { if (!saving) setOpened(false); }} title='Rename file' centered zIndex={600}
        closeOnEscape={!saving} closeOnClickOutside={!saving} withCloseButton={!saving}>
        <form onSubmit={(event) => { event.preventDefault(); void save(); }}>
          <Stack>
            <TextInput label='File name' value={name} onChange={(event) => setName(event.currentTarget.value)}
              description={extension ? `The ${extension} extension will be preserved.` : undefined}
              error={error} disabled={saving} data-autofocus />
            <Group justify='flex-end'>
              <Button variant='default' disabled={saving} onClick={() => setOpened(false)}>Cancel</Button>
              <Button type='submit' loading={saving} disabled={!name.trim() || name.trim() === originalBase}>Rename</Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </>
  );
}
